import { createSlice, Slice } from '@reduxjs/toolkit';
import { initPaginatedListData } from 'modules/shared/constants';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import { RootStateOrAny } from 'react-redux';
import { ICategoryListItem } from '../interfaces/category';
import { ICard, ICardBase, ICardListItem } from '../interfaces/cultureCard';
import { IGuideListItem } from '../interfaces/guide';

export const CULTURE_CARD_KEY_IN_STORE = 'cultureCard';

export interface ICultureCardState {
	isLoading: boolean;
	guides: IPaginatedData<IGuideListItem>;
	searchGuidesAutoComplete: {
		isLoading: boolean;
		result: IGuideListItem[];
	};

	createGuide: {
		isLoading: boolean;
		status: IReqStatus;
	};

	updateGuide: {
		isLoading: boolean;
		status: IReqStatus;
	};
	deleteGuide: IReqStatus;

	categories: IPaginatedData<ICategoryListItem>;
	searchCategoriesAutoComplete: {
		isLoading: boolean;
		result: ICategoryListItem[];
	};
	createCategory: {
		isLoading: boolean;
		status: IReqStatus;
	};

	updateCategory: {
		isLoading: boolean;
		status: IReqStatus;
	};
	deleteCategory: IReqStatus;

	cards: IPaginatedData<ICardListItem>;
	searchCardsAutoComplete: {
		isLoading: boolean;
		result: ICardBase[];
	};
	deleteCard: IReqStatus;
	createCard: IReqStatus;
	updateCard: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ICard;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
}

const initialState: ICultureCardState = {
	isLoading: false,
	guides: initPaginatedListData,
	searchGuidesAutoComplete: {
		isLoading: false,
		result: [],
	},
	createGuide: {
		isLoading: false,
		status: {
			error: false,
			success: false,
		},
	},
	updateGuide: {
		isLoading: false,
		status: {
			error: false,
			success: false,
		},
	},
	deleteGuide: {
		error: false,
		success: false,
	},

	categories: initPaginatedListData,
	searchCategoriesAutoComplete: {
		isLoading: false,
		result: [],
	},
	createCategory: {
		isLoading: false,
		status: {
			error: false,
			success: false,
		},
	},

	updateCategory: {
		isLoading: false,
		status: {
			error: false,
			success: false,
		},
	},
	deleteCategory: {
		error: false,
		success: false,
	},

	cards: initPaginatedListData,

	searchCardsAutoComplete: {
		isLoading: false,
		result: [],
	},
	deleteCard: {
		error: false,
		success: false,
	},
	createCard: {
		error: false,
		success: false,
	},
	updateCard: {
		itemToUpdate: {
			isLoading: false,
			status: {
				error: false,
				success: false,
			},
		},
		status: {
			error: false,
			success: false,
		},
	},
};

const cultureCardSlice: Slice = createSlice({
	name: CULTURE_CARD_KEY_IN_STORE,
	initialState,
	reducers: {
		resetCultureCardData: () => {
			return initialState;
		},

		getGuides: (state: ICultureCardState) => {
			state.isLoading = true;
		},
		getGuidesSuccess: (state: ICultureCardState, action) => {
			state.isLoading = false;
			state.guides = action.payload;
		},
		getGuidesFailure: (state: ICultureCardState) => {
			state.isLoading = false;
			state.guides = initialState.guides;
		},

		createGuide: (state: ICultureCardState) => {
			state.createGuide.isLoading = true;
		},

		createGuideSuccess: (state: ICultureCardState) => {
			state.createGuide = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},
		createGuideFailure: (state: ICultureCardState) => {
			state.createGuide = {
				isLoading: false,
				status: {
					error: true,
					success: false,
				},
			};
		},
		resetCreateGuide: (state: ICultureCardState) => {
			state.createGuide = initialState.createGuide;
		},

		updateGuide: (state: ICultureCardState) => {
			state.updateGuide.isLoading = true;
		},

		updateGuideSuccess: (state: ICultureCardState, action) => {
			const updatedGuide = action.payload.data;
			state.guides.items = state.guides.items.map(guide =>
				guide.id === updatedGuide.id ? updatedGuide : guide,
			);
			state.updateGuide = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},
		updateGuideFailure: (state: ICultureCardState) => {
			state.updateGuide = {
				isLoading: false,
				status: {
					error: true,
					success: false,
				},
			};
		},
		resetUpdateGuide: (state: ICultureCardState) => {
			state.updateGuide = initialState.updateGuide;
		},

		deleteGuide: (state: ICultureCardState) => {
			state.isLoading = true;
		},
		deleteGuideSuccess: (state: ICultureCardState) => {
			state.isLoading = false;
			state.deleteGuide = {
				error: false,
				success: true,
			};
		},
		deleteGuideFailure: (state: ICultureCardState) => {
			state.isLoading = false;
			state.deleteGuide = {
				error: true,
				success: false,
			};
		},
		resetDeleteGuide: (state: ICultureCardState) => {
			state.deleteGuide = {
				error: false,
				success: false,
			};
		},

		getCategories: (state: ICultureCardState) => {
			state.isLoading = true;
		},
		getCategoriesSuccess: (state: ICultureCardState, action) => {
			state.isLoading = false;
			state.categories = action.payload;
		},
		getCategoriesFailure: (state: ICultureCardState) => {
			state.isLoading = false;
			state.categories = initialState.categories;
		},

		createCategory: (state: ICultureCardState) => {
			state.createCategory.isLoading = true;
		},

		createCategorySuccess: (state: ICultureCardState) => {
			state.createCategory = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},
		createCategoryFailure: (state: ICultureCardState) => {
			state.createCategory = {
				isLoading: false,
				status: {
					error: true,
					success: false,
				},
			};
		},
		resetCreateCategory: (state: ICultureCardState) => {
			state.createCategory = initialState.createCategory;
		},

		updateCategory: (state: ICultureCardState) => {
			state.updateCategory.isLoading = true;
		},

		updateCategorySuccess: (state: ICultureCardState, action) => {
			const updatedCategory = action.payload.data;
			state.categories.items = state.categories.items.map(category =>
				category.id === updatedCategory.id ? updatedCategory : category,
			);
			state.updateCategory = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},
		updateCategoryFailure: (state: ICultureCardState) => {
			state.updateCategory = {
				isLoading: false,
				status: {
					error: true,
					success: false,
				},
			};
		},
		resetUpdateCategory: (state: ICultureCardState) => {
			state.updateCategory = initialState.updateCategory;
		},

		deleteCategory: (state: ICultureCardState) => {
			state.isLoading = true;
		},
		deleteCategorySuccess: (state: ICultureCardState) => {
			state.isLoading = false;
			state.deleteCategory = {
				error: false,
				success: true,
			};
		},
		deleteCategoryFailure: (state: ICultureCardState) => {
			state.isLoading = false;
			state.deleteCategory = {
				error: true,
				success: false,
			};
		},
		resetDeleteCategory: (state: ICultureCardState) => {
			state.deleteCategory = {
				error: false,
				success: false,
			};
		},

		searchGuides: (state: ICultureCardState) => {
			state.searchGuidesAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchGuidesResult: (state: ICultureCardState, action) => {
			state.searchGuidesAutoComplete = {
				isLoading: false,
				result: action.payload.guides,
			};
		},
		resetSearchGuides: (state: ICultureCardState) => {
			state.searchGuidesAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		searchCategories: (state: ICultureCardState) => {
			state.searchCategoriesAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchCategoriesResult: (state: ICultureCardState, action) => {
			state.searchCategoriesAutoComplete = {
				isLoading: false,
				result: action.payload.categories,
			};
		},
		resetSearchCategories: (state: ICultureCardState) => {
			state.searchCategoriesAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		getCards: (state: ICultureCardState) => {
			state.isLoading = true;
		},
		getCardsSuccess: (state: ICultureCardState, action) => {
			state.isLoading = false;
			state.cards = action.payload;
		},
		getCardsFailure: (state: ICultureCardState) => {
			state.isLoading = false;
			state.cards = initialState.cards;
		},

		searchCards: (state: ICultureCardState) => {
			state.searchCardsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchCardsResult: (state: ICultureCardState, action) => {
			state.searchCardsAutoComplete = {
				isLoading: false,
				result: action.payload.cards,
			};
		},
		resetSearchCards: (state: ICultureCardState) => {
			state.searchCardsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		deleteCard: (state: ICultureCardState) => {
			state.isLoading = true;
		},
		deleteCardSuccess: (state: ICultureCardState) => {
			state.isLoading = false;
			state.deleteCard = {
				error: false,
				success: true,
			};
		},
		deleteCardFailure: (state: ICultureCardState) => {
			state.isLoading = false;
			state.deleteCard = {
				error: true,
				success: false,
			};
		},
		resetDeleteCard: (state: ICultureCardState) => {
			state.deleteCard = {
				error: false,
				success: false,
			};
		},

		createCard: (state: ICultureCardState) => {
			state.isLoading = true;
		},
		createCardSuccess: (state: ICultureCardState) => {
			state.createCard = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createCardFailure: (state: ICultureCardState) => {
			state.createCard = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getCardToUpdate: (state: ICultureCardState) => {
			state.updateCard.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getCardToUpdateSuccess: (state: ICultureCardState, action) => {
			state.updateCard.itemToUpdate = {
				isLoading: false,
				data: action.payload.card,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getCardToUpdateFailure: (state: ICultureCardState) => {
			state.updateCard.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateCard: (state: ICultureCardState) => {
			state.isLoading = true;
		},
		updateCardSuccess: (state: ICultureCardState) => {
			state.updateCard.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateCardFailure: (state: ICultureCardState) => {
			state.updateCard.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
	},
});

export const {
	resetCultureCardData,

	getGuides,
	getGuidesSuccess,
	getGuidesFailure,

	createGuide,
	createGuideSuccess,
	createGuideFailure,
	resetCreateGuide,

	updateGuide,
	updateGuideSuccess,
	updateGuideFailure,
	resetUpdateGuide,

	deleteGuide,
	deleteGuideSuccess,
	deleteGuideFailure,
	resetDeleteGuide,

	getCategories,
	getCategoriesSuccess,
	getCategoriesFailure,

	createCategory,
	createCategorySuccess,
	createCategoryFailure,
	resetCreateCategory,

	updateCategory,
	updateCategorySuccess,
	updateCategoryFailure,
	resetUpdateCategory,

	deleteCategory,
	deleteCategorySuccess,
	deleteCategoryFailure,
	resetDeleteCategory,

	searchGuides,
	setSearchGuidesResult,
	resetSearchGuides,

	searchCategories,
	setSearchCategoriesResult,
	resetSearchCategories,

	getCards,
	getCardsSuccess,
	getCardsFailure,

	searchCards,
	setSearchCardsResult,
	resetSearchCards,

	deleteCard,
	deleteCardSuccess,
	deleteCardFailure,
	resetDeleteCard,

	createCard,
	createCardSuccess,
	createCardFailure,

	getCardToUpdate,
	getCardToUpdateSuccess,
	getCardToUpdateFailure,

	updateCard,
	updateCardSuccess,
	updateCardFailure,
} = cultureCardSlice.actions;

export const CultureCardStateSelector = (
	state: RootStateOrAny,
): ICultureCardState => state[CULTURE_CARD_KEY_IN_STORE];

export default cultureCardSlice;
